/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  HostBinding,
  input,
  output,
  forwardRef,
  computed,
  booleanAttribute,
  signal,
  effect,
  linkedSignal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssSwitchComponent } from '../switch/switch.component';
import { UmssLabelComponent } from '../label/label.component';

@Component({
  selector: 'umss-switch-group',
  standalone: true,
  imports: [CommonModule, UmssSwitchComponent, UmssLabelComponent],
  templateUrl: './switch-group.component.html',
  styleUrls: ['./switch-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssSwitchGroupComponent),
      multi: true
    }
  ]
})
export class UmssSwitchGroupComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  /** Content props */
  label = input<string>('Label');
  helperText = input<string | undefined>(undefined);
  ariaLabel = input<string | undefined>(undefined);

  /** Variant props */
  size = input<'Small' | 'Regular' | 'Large'>('Regular');
  layout = input<'Inline' | 'Block'>('Inline');
  fullWidth = input(false, { transform: booleanAttribute });
  switchPosition = input<'Left' | 'Right'>('Left');

  /** State props */
  disabled = input(false, { transform: booleanAttribute });
  required = input(false, { transform: booleanAttribute });

  isDisabled = linkedSignal(() => this.disabled());

  /** Value prop */
  private readonly _checked = signal(false);
  readonly checkedValue = computed(() => this._checked());

  /** Support for [checked] input alongside CVA */
  readonly checked = input<boolean | undefined>(undefined);

  /** Event outputs */
  checkedChange = output<boolean>();
  change = output<boolean>();

  /** Computed properties */
  computedAriaLabel = computed(
    () => this.ariaLabel() || this.label() || 'Toggle switch'
  );

  constructor() {
    // Sync external checked input with internal state
    effect(() => {
      const externalVal = this.checked();
      if (externalVal !== undefined) {
        this._checked.set(externalVal);
      }
    });
  }

  /** ControlValueAccessor implementation */
  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: boolean): void {
    this._checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  /** Event handlers */
  onSwitchChange(value: boolean): void {
    if (this.disabled()) return;

    this._checked.set(value);
    this._onChange(value);
    this._onTouched();
    this.checkedChange.emit(value);
    this.change.emit(value);
  }
}
